import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_HEIGHT_RATIO = 0.3;
const MIN_HEIGHT_RATIO = 0.2;
const MAX_HEIGHT_RATIO = 0.85;
const MIN_HEIGHT_PX = 220;
const RESIZE_STEP = 24;

const getViewportHeight = () => {
    if (typeof window === 'undefined') {
        return 800;
    }
    return window.innerHeight || 800;
};

const getContainerHeight = (containerRef) => {
    const element = containerRef?.current;
    if (!element) {
        return null;
    }
    const height = element.clientHeight || element.getBoundingClientRect?.().height || 0;
    return Number.isFinite(height) && height > 0 ? height : null;
};

export default function useResizableBottomSheet(options = {}) {
    const {
        containerRef,
        defaultHeightRatio = DEFAULT_HEIGHT_RATIO,
        minHeightRatio = MIN_HEIGHT_RATIO,
        maxHeightRatio = MAX_HEIGHT_RATIO,
        minHeightPx = MIN_HEIGHT_PX,
        resizeStep = RESIZE_STEP,
    } = options;

    const getAvailableHeight = useCallback(() => (
        getContainerHeight(containerRef) ?? getViewportHeight()
    ), [containerRef]);

    const getBounds = useCallback((availableHeight = getAvailableHeight()) => {
        const max = Math.round(availableHeight * maxHeightRatio);
        const rawMin = Math.max(minHeightPx, Math.round(availableHeight * minHeightRatio));
        const min = Math.min(rawMin, max);
        return { min, max };
    }, [getAvailableHeight, maxHeightRatio, minHeightPx, minHeightRatio]);

    const clampHeight = useCallback((value, availableHeight = getAvailableHeight()) => {
        const { min, max } = getBounds(availableHeight);
        return Math.max(min, Math.min(value, max));
    }, [getAvailableHeight, getBounds]);

    const [height, setHeight] = useState(() => {
        const initialHeight = Math.round(getAvailableHeight() * defaultHeightRatio);
        return clampHeight(initialHeight);
    });
    const [isResizing, setIsResizing] = useState(false);
    const heightRef = useRef(height);
    const dragRef = useRef({ startY: 0, startHeight: height });

    useEffect(() => {
        heightRef.current = height;
    }, [height]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }
        const handleResize = () => {
            setHeight((current) => clampHeight(current, getAvailableHeight()));
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [clampHeight, getAvailableHeight]);

    useEffect(() => {
        const node = containerRef?.current;
        if (!node) {
            return undefined;
        }

        const updateFromContainer = () => {
            setHeight((current) => clampHeight(current));
        };

        updateFromContainer();

        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const resizeObserver = new ResizeObserver(updateFromContainer);
        resizeObserver.observe(node);

        return () => resizeObserver.disconnect();
    }, [clampHeight, containerRef]);

    const updateHeight = useCallback((nextHeight) => {
        const clampedHeight = clampHeight(nextHeight);
        heightRef.current = clampedHeight;
        setHeight(clampedHeight);
    }, [clampHeight]);

    const handleResizeMove = useCallback((event) => {
        const clientY = event?.clientY;
        if (!Number.isFinite(clientY)) {
            return;
        }
        const { startY, startHeight } = dragRef.current;
        const delta = startY - clientY;
        updateHeight(startHeight + delta);
    }, [updateHeight]);

    const stopResize = useCallback(() => {
        setIsResizing(false);
        if (typeof document !== 'undefined') {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }
        window.removeEventListener('pointermove', handleResizeMove);
        window.removeEventListener('pointerup', stopResize);
        window.removeEventListener('pointercancel', stopResize);
    }, [handleResizeMove]);

    const startResize = useCallback((event) => {
        if (event.button != null && event.button !== 0) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const clientY = event?.clientY;
        if (!Number.isFinite(clientY)) {
            return;
        }
        dragRef.current = {
            startY: clientY,
            startHeight: heightRef.current,
        };
        setIsResizing(true);
        if (typeof document !== 'undefined') {
            document.body.style.cursor = 'ns-resize';
            document.body.style.userSelect = 'none';
        }
        window.addEventListener('pointermove', handleResizeMove);
        window.addEventListener('pointerup', stopResize);
        window.addEventListener('pointercancel', stopResize);
    }, [handleResizeMove, stopResize]);

    const handleKeyDown = useCallback((event) => {
        if (!event) {
            return;
        }
        const { min, max } = getBounds();
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            updateHeight(heightRef.current + resizeStep);
        } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            updateHeight(heightRef.current - resizeStep);
        } else if (event.key === 'Home') {
            event.preventDefault();
            updateHeight(min);
        } else if (event.key === 'End') {
            event.preventDefault();
            updateHeight(max);
        }
    }, [getBounds, resizeStep, updateHeight]);

    useEffect(() => () => {
        if (typeof window === 'undefined') {
            return;
        }
        window.removeEventListener('pointermove', handleResizeMove);
        window.removeEventListener('pointerup', stopResize);
        window.removeEventListener('pointercancel', stopResize);
        if (typeof document !== 'undefined') {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }
    }, [handleResizeMove, stopResize]);

    return {
        height,
        isResizing,
        startResize,
        handleKeyDown,
        updateHeight,
    };
}
